import React from 'react';
import { withRouter } from 'react-router';
import autoBind from 'react-autobind';
import _ from 'lodash';
import ClassNames from 'classnames';

import NavigationBar from './NavigationBar.jsx';
import InlineMessage from './Common/InlineMessage.jsx';
import Spinner from './Common/Spinner.jsx';
import TextInput from './Common/TextInput.jsx';

import NetCodeEntryStore from '../stores/NetCodeEntryStore.js';
import ApplicationStore from '../stores/ApplicationStore';
import ContentService from '../services/ContentService.js';
import Formatter from '../utils/Formatter.js';

import Constants from '../constants/IDVerificationConstants.js';
import AppConstants from '../constants/ApplicationConstants.js';

import Transitions from '../core/Transitions.js';
import IDVerificationActions from '../actions/IDVerificationActions.js';
import ApplicationActions from '../actions/ApplicationActions.js';

import Footer from './Footer.jsx';
import FooterUtil from './../utils/FooterHelper';
import DownloadFormsHelper from '../utils/DownloadFormsHelper.js';
import ApplicationHelper from '../utils/ApplicationHelper.js';
import OmnitureTracking from '../utils/OmnitureTracking.js';

const SendNetCodeResults = Constants.SendNetCodeResults;
const VerifyNetCodeResults = Constants.VerifyNetCodeResults;
const YesNoValues = AppConstants.YesNoValues;
function getStateFromStore() {
    return {
        product: NetCodeEntryStore.getProduct(),
        content: ContentService.getContent(),
        showMoreMenu: NetCodeEntryStore.getShowHideMore(),
        sendingResult: NetCodeEntryStore.getSendNetCodeResult(),
        data: NetCodeEntryStore.getNetCodeData(),
        isJoint: NetCodeEntryStore.getIsJoint(),
        verifyResult: NetCodeEntryStore.getVerifyResult(),
        resending: false,
        waiting: false,
        disableJointResend: NetCodeEntryStore.getDisableJointResend(),
        formModel: NetCodeEntryStore.getFormModel(),
        consolidatedFormModel: NetCodeEntryStore.getConsolidatedFormModel(),
        applicationId: NetCodeEntryStore.getApplicationId(),
        accountNumber: NetCodeEntryStore.getAccountNumber(),
        hasSubmitFailed: NetCodeEntryStore.getHasSubmitFailed(),
        showDownloadMenu: NetCodeEntryStore.getShowDownloadMenu(),
        formsToDownload: NetCodeEntryStore.getFormsToDownload(),
        enableDownloadButton: NetCodeEntryStore.getEnableDownloadButton(),
        accountType: NetCodeEntryStore.getAccountType(),
        downloadInProgress: NetCodeEntryStore.getDownloadInProgress(),
        selectedForms: NetCodeEntryStore.getSelectedForms(),
        readyForDownload: NetCodeEntryStore.getReadyForDownload(),
        downloadedForms: NetCodeEntryStore.getDownloadedForms(),
        formDownloaded:NetCodeEntryStore.getIsFormsDownloaded(),
        downloadFormsFailed: NetCodeEntryStore.getDownloadFormsFailed(),
        clientIdentificationMode: ApplicationStore.getClientIdentificationMode()
    };
}


class NetCodeEntry extends React.Component {
    constructor(props) {
        super(props);
        autoBind(this);
        this.state = getStateFromStore();
    }

    componentDidMount() {
        NetCodeEntryStore.addChangeListener(this._onChange);
        OmnitureTracking.getTrackingEnabled();
    }

    componentWillUnmount() {
        NetCodeEntryStore.removeChangeListener(this._onChange)
    }

    componentWillUpdate(nextProps, nextState) {
        if (nextState.verifyResult === VerifyNetCodeResults.VerifyNetCodeSuccess) {
            IDVerificationActions.sendDigitalConsent(nextState.formModel.hasDigitalSignature, nextState.formModel.DigitalSignatureTimeStamp);
            IDVerificationActions.navigateToCongratulations(nextState.product.value, nextState.formModel);
            if (!this.state.accountNumber) {
                const applicationForSubmit = ApplicationHelper.getApplicationWithoutPredictiveSearchAdviceFee(nextState.formModel);
                ApplicationActions.submitApplication(nextState.product.value, applicationForSubmit, nextState.consolidatedFormModel.form.investmentAllocation.investmentOptions);
            }
        }
        if (nextState.data.isValidated) {
            this.setState({ waiting: true });
            let unverified = [];
            for (let i = 0; i < this.state.data.items.length; i++) {
                if (!this.state.data.items[i].verified) {
                    unverified.push(this.state.data.items[i]);
                }
            }
            const mobileNumbers = _.pluck(unverified, 'mobileNumber'),
                netCodes = _.pluck(unverified, 'netCode');
            IDVerificationActions.submitNetCodes(this.state.product.value, mobileNumbers, netCodes, this.state.applicationId, this.state.data.items.length);
        }

        if (this.state.generateResult !== nextState.generateResult) {
            this.setState({ resending: false});
        }

        if (!this.state.accountNumber && nextState.accountNumber){
            Transitions.forwardFromNetCodeEntry(this.props.router, { valid: true });
        }
    }

    _onChange() {
        this.setState(getStateFromStore());
    }

    submit() {
        if (this.state.accountNumber){
            Transitions.forwardFromNetCodeEntry(this.props.router, { valid: true });
        }
        else{
            IDVerificationActions.validateNetCode();    
        }
    }

    resend() {
        this.setState({ resending: true });
        let itemsToResend = [];
        for (let i = 0; i < this.state.data.items.length; i++) {
            if (this.state.data.items[i].resend === YesNoValues.YES) {
                itemsToResend.push(this.state.data.items[i]);
            }
        }
        const mobileNumbersToResend = _.pluck(itemsToResend, 'mobileNumber');
        IDVerificationActions.resendNetCode(this.state.product.value, mobileNumbersToResend, this.state.isJoint, this.state.applicationId);
    }

    print() {
        if (!this.state.accountNumber) {
            const applicationForSubmit = ApplicationHelper.getApplicationWithoutPredictiveSearchAdviceFee(this.state.formModel);
            ApplicationActions.submitApplication(this.state.product.value, applicationForSubmit, this.state.consolidatedFormModel.form.investmentAllocation.investmentOptions);
        }
    }

    handleClickSkip() {
        if (!this.state.accountNumber) {
            const applicationForSubmit = ApplicationHelper.getApplicationWithoutPredictiveSearchAdviceFee(this.state.formModel);
            ApplicationActions.submitApplication(this.state.product.value, applicationForSubmit, this.state.consolidatedFormModel.form.investmentAllocation.investmentOptions);
        }
    }

    updateSingleModel(event) {
        this.updateStoreModel(event.target.value, event.target.model, 0);
    }

    updateJointModel(event) {
        const split = event.target.id.split('-'), index = split[2];
        this.updateStoreModel(event.target.value, event.target.model, index);
    }

    updateJointCheckBox(event) {
        const split = event.target.id.split('-'), index = split[2];
        this.updateStoreModel(this.state.data.items[index].resend, 'resend', index);
    }

    updateStoreModel(value, fieldId, index) {
        IDVerificationActions.updateNetCodeModel(value, fieldId, index);
    }

    getDisplayMode() {
        switch (this.state.sendingResult) {
            case SendNetCodeResults.LimitReached:
            case SendNetCodeResults.VerifyLimitReached:
                return this.renderLimitReached();
            case SendNetCodeResults.SendNetCodeSuccess:
                return this.renderCodeEntry();
            case SendNetCodeResults.InvalidMobileNo:
            case SendNetCodeResults.ServerError:
                return this.renderError();
            default:
                return this.renderWaiting();
        }
    }

    renderLimitReached() {
        const message = this.state.sendingResult === SendNetCodeResults.LimitReached
            ? this.state.content.netcodeEntry.limitReached
            : this.state.content.netcodeEntry.verifyLimitReached;
        return (
            <div>
                <div className="container netcode-entry-page__section">
                    <InlineMessage id="netCodeEntry"
                        isHidden={false}
                        type={AppConstants.messageTypes.errorNoLine}
                        message={message} />
                </div>
            </div>);
    }

    renderError() {
        return (
            <div>
                <div className="container netcode-entry-page__section">
                    <div id="netCodeError" className="alert alert-warning-no-line">
                        <h4>Message sending failure</h4>
                        <p>We are currently experiencing technical difficulties.You could -</p>
                        <ul>
                            <li>Try again by clicking on 'Resend code', OR</li>
                            <li>Click <a onClick={this.handleClickSkip}>skip</a> to proceed to obtain an account number by clicking on the bar below to download and print the application form, sign and send it to us. This account number will be used to set up your account.</li>
                        </ul>
                    </div>
                </div>
            </div>);
    }

    renderWaiting() {
        return (
            <div>
                <div className="container netcode-entry-page__section">
                    <Spinner size={AppConstants.SpinnerSizes.Three} />
                </div>
            </div>);
    }

    renderSingleEntry() {
        const copy = Formatter.formatString(this.state.content.netcodeEntry.generateSingleSuccess, { mobileNumber: this.state.data.items[0].masked });
        return (
            <div>
                <div className="container netcode-entry-page__section" dangerouslySetInnerHTML={{ __html: copy }}></div>
                <div className="container netcode-entry-page__section">
                    <TextInput id="netCodeSingleEntry"
                        value={this.state.data.items[0].netCode}
                        errorList={this.state.data.items[0].errorList}
                        model="netCode"
                        onBlur={this.updateSingleModel}
                        label="Enter code here"
                        customClass="netcode-entry-page__netcode-div"
                        className="netcode-entry-page__input"/>
                </div>
            </div>);
    }

    renderMultipleEntries() {
        const content = this.state.content.netcodeEntry.generateJointSuccess,
            update = this.updateJointModel,
            handleClick = this.updateJointCheckBox,
            idConstant = Constants.NetCodeModelIds.Joint,
            yesValue = YesNoValues.YES;
        let renderRow = function (data, index) {
            const copy = Formatter.formatString(content, { fullName: data.name, mobileNumber: data.masked }),
                idKey = idConstant + '-' + index,
                textKey = 'txt-' + idKey,
                checkKey = 'chk-' + idKey;
            return (
                <div key={idKey}>
                    <div className="container netcode-entry-page__section" dangerouslySetInnerHTML={{ __html: copy }}></div>
                    <div className="container netcode-entry-page__section">
                        <TextInput id={textKey}
                            value={data.netCode}
                            errorList={data.errorList}
                            model="netCode"
                            isDisabled={data.disabled}
                            onBlur={update}
                            label="Enter code here"
                            customClass="netcode-entry-page__netcode-div"
                            className="netcode-entry-page__input"/>
                        <div className="form-select">
                            <label htmlFor={checkKey} tabIndex="0" className={ClassNames({ 'btn-cfs': data.resend === yesValue }) }>
                                <i className="icon icon-check"></i>
                                <input id={checkKey} tabIndex="-1" type="checkbox" className="btn-option btn btn-default btn-lg" value={yesValue} onChange={handleClick} checked={data.resend === yesValue} />
                            </label><span className="netcode-entry-page__checkbox-label">Select to resend code</span>
                        </div>
                    </div>
                </div>);

        };
        return (<div>
            {this.state.data.items.map(function (data, index) {
                return renderRow(data, index);
            }) }
        </div>)

    }

    renderCodeEntry() {
        if (this.state.isJoint) {
            return this.renderMultipleEntries();
        } else {
            return this.renderSingleEntry();
        }
    }

    renderEntryControls() {
        if (this.state.isJoint) {
            return this.renderJointControls();
        } else {
            return this.renderSingleControls();
        }
    }

    renderJointControls() {
        return (<div className="navbar navigationBar">
                <button type="button"
                        disabled={this.state.disableJointResend}
                        className="btn btn-default btn-large netcode-entry-page-controls__resendBtn"
                        onClick={this.resend}><span className="glyphicon glyphicon-menu-left pull-left" /> Resend code {this.state.resending ? <Spinner inline={true}/> : null}</button>
                <button type="button"
                        disabled={this.state.resending || this.state.waiting}
                        className="btn btn-info btn-large netcode-entry-page-controls__submitBtn"
                        onClick={this.submit}><span className="glyphicon glyphicon-menu-right pull-right" /> Submit {this.state.waiting ? <Spinner inline={true}/> : null}</button>
            </div>);
    }

    renderSingleControls(){
        return (<div className="navbar navigationBar">
                    <button type="button"
                        disabled={this.state.resending || this.state.waiting}
                        className="btn btn-default btn-large netcode-entry-page-controls__resendBtn"
                        onClick={this.resend}><span className="glyphicon glyphicon-menu-left pull-left" /> Resend code {this.state.resending ? <Spinner inline={true}/> : null}</button>
                    <button type="button"
                        disabled={this.state.resending || this.state.waiting}
                        className="btn btn-info btn-large netcode-entry-page-controls__submitBtn"
                        onClick={this.submit}><span className="glyphicon glyphicon-menu-right pull-right" /> Submit {this.state.waiting ? <Spinner inline={true}/> : null}</button>
                </div>);
    }

    renderLimitReachedControls(){
        return (<button className="btn navbar navbar-info navigationBar" onClick={this.print}>
                    <div className="container">
                        <div className="pull-left"><h3>Print application and obtain account number</h3></div>
                        <div className="pull-right"><a className="arrow"><i className="icon icon-right-open-big"></i></a></div>
                    </div>
                </button>);
    }

    renderWaitingControls(){
        return (<div className="navbar navigationBar">
                        <button type="button"
                                disabled={true}
                                className="btn btn-default btn-large netcode-entry-page-controls__resendBtn" >
                                <span className="glyphicon glyphicon-menu-left pull-left" /> Resend code </button>
                        <button type="button"
                                disabled={true}
                                className="btn btn-info btn-large netcode-entry-page-controls__submitBtn" >
                                <span className="glyphicon glyphicon-menu-right pull-right" /> Submit </button>
                </div>);
    }


    getControls(){
        switch (this.state.sendingResult) {
            case SendNetCodeResults.LimitReached:
            case SendNetCodeResults.VerifyLimitReached:
                return this.renderLimitReachedControls();
            case SendNetCodeResults.SendNetCodeSuccess:
                return this.renderEntryControls();
            case SendNetCodeResults.InvalidMobileNo:
            case SendNetCodeResults.ServerError:
                return this.renderSingleControls();
            default:
                return this.renderWaitingControls();
        }
    }

    render() {
        let displayMode = this.getDisplayMode();
        const footerContent = FooterUtil.getContent(this.state.product.value, this.state.content.footer);
        const downloadData = DownloadFormsHelper.createDownloadData(this.state);
        const dockFooterStyle = FooterUtil.adjustViewport();
        let controls = this.getControls();
        return (
            <div className="netcode-entry-page">
                <div id="netcode-entry" style={dockFooterStyle}>
                <NavigationBar  
                    product={this.state.product} 
                    route={this.props.route.path} 
                    showMoreMenu={this.state.showMoreMenu} 
                    moreMenuContent={this.state.content.moreMenu}
                    application={this.state.formModel}
                    clientIdentificationMode={this.state.clientIdentificationMode}
                    showDownloadMenu={this.state.showDownloadMenu}
                    accountType={this.state.accountType}
                    download={downloadData}/>
                <nav className="navbar-sub-sectionName navbar navbar-sub navigationBar">
                <div className="container">
                    <div className="pull-left">
                        <span className="heading-text">{this.state.content.netcodeEntry.pageTitle}</span>
                    </div>
                </div>
                </nav>
                {displayMode}
                </div>
                {controls}
                <Footer content={footerContent} />
            </div>);
    }
}

export default withRouter(NetCodeEntry)


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./components/NetCodeEntry.jsx